import { useAppHeaderHidden } from "apollo/components/mobile/tab-bar-context";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React from "react";
import { cn } from "ui/lib/utils";
import { useHomeHref } from "apollo/lib/last-team";
import { VersionTracker } from "./version-tracker";

const Navigation = dynamic(
  () => import("./navigation").then((mod) => ({
    default: mod.Navigation
  })),
  {
    ssr: false,
    placeholder: <div className="h-24">hello</div>,
  }
);
const TeamsDropdown = dynamic(
  () => import("./teams-dropdown").then((mod) => ({
    default: mod.TeamsDropdown
  })),
  {
    ssr: false,
  }
);
const UserDropdown = dynamic(
  () => import("./user-dropdown").then((mod) => ({
    default: mod.UserDropdown
  })),
  {
    ssr: false,
  }
);

// The server paints this until the client-only bar loads, so the header is
// there from the first frame.
const MobileHeaderFrame = () => (
  <div className="flex items-center gap-2 h-12 px-3 bg-background border-b border-white/[0.06]">
    <span className="flex items-center justify-center h-11 w-11 -ml-2 flex-shrink-0">
      <img className="w-auto h-5" src="/assets/logo/white-icon.svg" alt="" />
    </span>
    <span className="h-4 w-32 rounded bg-white/[0.06] motion-safe:animate-pulse" />
  </div>
);

const MobileHeaderBar = dynamic(
  () => import("./mobile-header").then((mod) => ({
    default: mod.MobileHeader
  })),
  {
    ssr: false,
    loading: () => <MobileHeaderFrame />,
  }
);

// The bar's height is reserved on the server so pages that measure
// #top-navigation on mount see it before the client-only bar loads.
export const MobileHeader = () => {
  const hidden = useAppHeaderHidden();
  return (
    <div className={cn("h-12 lg:hidden", hidden && "hidden")}>
      <MobileHeaderBar />
    </div>
  );
};

const Logo = React.memo(() => {
  return (
    <img
      className="hidden w-auto h-5 md:block"
      src={`/assets/logo/white-icon.svg`}
      alt="Apollo Logo"
    />
  );
});

export const Header = React.memo(() => {
  const { pathname, asPath, push, query } = useRouter();

  const home = useHomeHref();
  const handleLogoClick = React.useCallback(() => {
    push(home);
  }, [push, home]);

  return (
    <nav
      key={query?.bootcampId}
      className="hidden bg-background border-b border-white/[0.06] lg:block"
    >
      <div className="hidden lg:block w-full">
        <div className="w-full px-4 py-2">
          <div className="flex items-center justify-center relative w-full min-h-[40px]">
            <div className="flex items-center absolute left-0 gap-2">
              <button
                onClick={handleLogoClick}
                className="rounded flex items-center flex-shrink-0"
              >
                <Logo />
              </button>
              <div aria-hidden="true" className="text-white/10 text-lg font-light select-none">/</div>
              <TeamsDropdown />
            </div>
            <Navigation />
            <div className="absolute right-0 flex items-center">
              <VersionTracker>
                <UserDropdown />
              </VersionTracker>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
});
